import React, { Component } from "react";
import CommonModal from "components/CommonModal";
import { connect, ConnectedProps } from "react-redux";
import { State } from "state";
import { fetchChains } from "state/chains";
import { fetchStores } from "state/stores";
import { Chain } from "interfaces/chain";
import { Store } from "interfaces/store";
import { getRoles, Role } from "state/chain_user_role";
import Constants from "helpers/constants";
import Loader from "components/Loader";
import * as XLSX from "xlsx";
import { readFileWithEncoding, csvToArray } from "utils/fileEditorHelpers";

// Types 

interface AssignChainStoreModalProps extends PropsFromRedux {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: AssignmentData) => Promise<void>;
    stage?: any;
}

interface AssignChainStoreModalState {
    selectedChainId: number | null;
    selectedStores: number[];
    selectedRole: string;
    openChainDropdown: boolean;
    openStoreDropdown: boolean;
    openRoleDropdown: boolean;
    isSubmitting: boolean;
    isLoadingStores: boolean;
    filteredStores: Store[];
    chainSearchTerm: string;
    storeSearchTerm: string;
    roleSearchTerm: string;
    isParsingCsv: boolean;
    csvUploadMessage: string;
    csvUploadError: string;
}

export interface AssignmentData {
    chainId: number;
    storeIds: number[];
    role: string;
    roleText?: string;
}

//  Styles (non-layout only — layout handled via SCSS classes)

const inlineStyles = {
    modalHeader: {
        display: "flex",
        alignItems: "center",
        gap: "16px",
        marginBottom: "10px",
    } as React.CSSProperties,
    modalMessage: {
        fontSize: "12px",
        color: "#666",
        marginBottom: "10px",
        lineHeight: 1.5,
    } as React.CSSProperties,
    modalFormCard: {
        backgroundColor: "#F5F5F5",
        borderRadius: "5px",
        padding: "20px",
    } as React.CSSProperties,
    modalLabel: {
        fontSize: "14px",
        fontWeight: 500,
        color: "#333",
    } as React.CSSProperties,
    selectedStoreChip: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        backgroundColor: "#E5B611",
        color: "#FFFFFF",
        padding: "6px 10px",
        borderRadius: "16px",
        fontSize: "12px",
        fontWeight: 500,
        margin: "4px",
    } as React.CSSProperties,
    removeStoreButton: {
        backgroundColor: "transparent",
        border: "none",
        color: "#FFFFFF",
        cursor: "pointer",
        fontSize: "16px",
        fontWeight: "bold",
        padding: "0",
        width: "16px",
        height: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        transition: "background-color 0.2s",
    } as React.CSSProperties,
    infoPill: {
        backgroundColor: "#E8F0FB",
        border: "1px solid #0755AA",
        borderRadius: "10px",
        padding: "10px 16px",
        fontSize: "12px",
        color: "#0755AA",
        fontWeight: 500,
        marginTop: "20px",
        marginBottom: "24px",
        display: "flex",
        alignItems: "center",
        gap: 6,
    } as React.CSSProperties,
    searchInput: {
        width: "100%",
        padding: "8px 12px",
        fontSize: "12px",
        border: "1px solid #E0E0E0",
        borderRadius: "4px",
        outline: "none",
        marginBottom: "4px",
        boxSizing: "border-box",
    } as React.CSSProperties,
} as const;

// Component 

class SelectChainStoreInfo extends Component<AssignChainStoreModalProps, AssignChainStoreModalState> {
    private chainDropdownRef: React.RefObject<HTMLDivElement>;
    private storeDropdownRef: React.RefObject<HTMLDivElement>;
    private roleDropdownRef: React.RefObject<HTMLDivElement>;
    private csvFileInputRef: React.RefObject<HTMLInputElement>;

    constructor(props: AssignChainStoreModalProps) {
        super(props);
        this.state = {
            selectedChainId: null,
            selectedStores: [],
            selectedRole: "all",
            openChainDropdown: false,
            openStoreDropdown: false,
            openRoleDropdown: false,
            isSubmitting: false,
            isLoadingStores: false,
            filteredStores: [],
            chainSearchTerm: "",
            storeSearchTerm: "",
            roleSearchTerm: "",
            isParsingCsv: false,
            csvUploadMessage: "",
            csvUploadError: "",
        };
        this.chainDropdownRef = React.createRef();
        this.storeDropdownRef = React.createRef();
        this.roleDropdownRef = React.createRef();
        this.csvFileInputRef = React.createRef();
    }

    componentDidMount() {
        document.addEventListener("click", this.handleClickOutside);
        if (this.props.user?.role === Constants.AB_SYSTEM_ADMIN) {
            this.props.fetchChains();
        }
        this.props.getRoles();
    }

    componentWillUnmount() {
        document.removeEventListener("click", this.handleClickOutside);
    }

    componentDidUpdate(prevProps: AssignChainStoreModalProps, prevState: AssignChainStoreModalState) {
        if (this.props.isOpen && !prevProps.isOpen) {
            const { stage } = this.props;
            const preSelectedChainId = stage?.chainId ?? null;
            if (prevProps.stores !== this.props.stores && this.state.isLoadingStores) {
                this.setState({ isLoadingStores: false });
            }
            this.setState({
                selectedChainId: preSelectedChainId,
                selectedStores: [],
                selectedRole: "all",
                openChainDropdown: false,
                openStoreDropdown: false,
                openRoleDropdown: false,
                isSubmitting: false,
                isLoadingStores: false,
                filteredStores: [],
                chainSearchTerm: "",
                storeSearchTerm: "",
                roleSearchTerm: "",
                isParsingCsv: false,
                csvUploadMessage: "",
                csvUploadError: "",
            });

            if (this.props.user?.role === Constants.AB_SYSTEM_ADMIN) {
                this.setState({ isLoadingStores: true });
                this.props.fetchStores({ page: 0, size: 100000000 });
            } else if (preSelectedChainId) {
                this.setState({ isLoadingStores: true });
                this.props.fetchStores({ chainId: preSelectedChainId, page: 0, size: 100000000 });
            }
        }

        if (
            prevProps.stores !== this.props.stores ||
            prevState.selectedChainId !== this.state.selectedChainId
        ) {
            this.updateFilteredStores();
        }
    }

    updateFilteredStores = () => {
        const { selectedChainId } = this.state;
        const { stores = [], stage } = this.props;

        if (selectedChainId && stores.length > 0) {
            const filtered = stores.filter((store: Store) => store.chain_id === selectedChainId);
            let preSelectedStores: number[] = [];
            if (stage?.stores && stage.stores.length > 0 && this.state.selectedStores.length === 0) {
                preSelectedStores = filtered
                    .filter((store) => stage.stores.includes(store.retail_store_num))
                    .map((store) => store.id);
            }

            const newState: Partial<AssignChainStoreModalState> = {
                filteredStores: filtered,
                isLoadingStores: false,
            };
            if (preSelectedStores.length > 0) newState.selectedStores = preSelectedStores;
            this.setState(newState as AssignChainStoreModalState);
        } else {
            this.setState({ filteredStores: [], isLoadingStores: false });
        }
    };

    handleChainFilterChange = (chainId: number) => {
        this.setState(
            {
                selectedChainId: chainId,
                openChainDropdown: false,
                selectedStores: [],
                selectedRole: "all",
                isLoadingStores: true,
                chainSearchTerm: "",
            },
            () => this.updateFilteredStores()
        );
    };

    handleClickOutside = (event: MouseEvent) => {
        if (this.chainDropdownRef.current && !this.chainDropdownRef.current.contains(event.target as Node)) {
            this.setState({ openChainDropdown: false, chainSearchTerm: "" });
        }
        if (this.storeDropdownRef.current && !this.storeDropdownRef.current.contains(event.target as Node)) {
            this.setState({ openStoreDropdown: false, storeSearchTerm: "" });
        }
        if (this.roleDropdownRef.current && !this.roleDropdownRef.current.contains(event.target as Node)) {
            this.setState({ openRoleDropdown: false, roleSearchTerm: "" });
        }
    };

    resetForm = () => {
        this.setState({
            selectedChainId: null,
            selectedStores: [],
            selectedRole: "all",
            openChainDropdown: false,
            openStoreDropdown: false,
            openRoleDropdown: false,
            isSubmitting: false,
            isLoadingStores: false,
            filteredStores: [],
            chainSearchTerm: "",
            storeSearchTerm: "",
            roleSearchTerm: "",
            isParsingCsv: false,
            csvUploadMessage: "",
            csvUploadError: "",
        });
    };

    handleStoreToggle = (storeId: number) => {
        const { selectedStores } = this.state;
        if (selectedStores.includes(storeId)) {
            this.setState({ selectedStores: selectedStores.filter((id) => id !== storeId) });
        } else {
            this.setState({ selectedStores: [...selectedStores, storeId] });
        }
    };

    handleSelectAllStores = () => {
        const { filteredStores, storeSearchTerm } = this.state;
        const filteredBySearch = this.filterStoresBySearch(filteredStores, storeSearchTerm);
        this.setState({ selectedStores: filteredBySearch.map((store) => store.id) });
    };

    handleClearAllStores = () => this.setState({ selectedStores: [] });

    handleRemoveStore = (storeId: number) => {
        this.setState({ selectedStores: this.state.selectedStores.filter((id) => id !== storeId) });
    };

    handleRoleFilterChange = (value: string) => {
        this.setState({ selectedRole: value, openRoleDropdown: false, roleSearchTerm: "" });
    };

    normalizeStoreNumber = (value: string) => value.replace(/\uFEFF/g, "").replace(/^"|"$/g, "").trim().toLowerCase();
    isAllowedUploadFileType = (fileName: string) => /\.(csv|xls|xlsx)$/i.test(fileName);
    
    getRowsFromUploadedFile = async (file: File): Promise<any[][]> => {
        if (/\.(xls|xlsx)$/i.test(file.name)) {
            const buffer = await file.arrayBuffer();
            const firstSheetName = XLSX.read(buffer, { type: "array" }).SheetNames[0];
            if (!firstSheetName) throw new Error("Uploaded Excel file has no sheets.");
            return XLSX.utils.sheet_to_json(XLSX.read(buffer, { type: "array" }).Sheets[firstSheetName], { header: 1, raw: false, defval: "" }) as any[][];
        }
        const text = await readFileWithEncoding(file);
        return csvToArray(text);
    };
    getStoreValuesFromRows = (rows: any[][]): { error?: string; values?: string[] } => {
        if (!rows || rows.length < 2) return { error: "File must include header and at least one store number." };
        const header = this.normalizeStoreNumber(String(rows[0]?.[0] ?? "")).replace(/\s+/g, "_");
        const values = rows.slice(1).map((row) => String(row?.[0] ?? "").trim()).filter(Boolean);
        if (header !== "retail_store_number") return { error: "Invalid header. Expected retail_store_number (or supported equivalent)." };
        if (values.length === 0) return { error: "No store values found in the uploaded file." };
        return { values };
    };
    getMatchedStoreIds = (values: string[], stores: Store[]) => {
        const storeMap = new Map<string, number>();
        stores.forEach((store) => { storeMap.set(this.normalizeStoreNumber(String(store.retail_store_num)), store.id); });
        return Array.from(new Set(values.map((value) => storeMap.get(this.normalizeStoreNumber(value))) .filter((id): id is number => typeof id === "number") ));
    };
    handleCsvUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        const inputEl = event.currentTarget;
        if (!file) return;
        if (!this.isAllowedUploadFileType(file.name)) { this.setState({ csvUploadError: "Please upload a valid file format: .csv, .xls, or .xlsx.", csvUploadMessage: "", }); inputEl.value = ""; return; }
        const { selectedChainId, filteredStores, isLoadingStores } = this.state;
        if (!selectedChainId) { this.setState({ csvUploadError: "Please select a chain before uploading a CSV.", csvUploadMessage: "" }); inputEl.value = ""; return;}
        if (isLoadingStores) { this.setState({ csvUploadError: "Stores are still loading. Please try again in a moment.", csvUploadMessage: "" }); inputEl.value = ""; return; }
        if (filteredStores.length === 0) { this.setState({ csvUploadError: "No stores available for the selected chain.", csvUploadMessage: "" }); inputEl.value = ""; return; }
        this.setState({ isParsingCsv: true, csvUploadError: "", csvUploadMessage: "" });

        try {
            const rows = await this.getRowsFromUploadedFile(file);
            const { values, error } = this.getStoreValuesFromRows(rows);
            if (error || !values) { this.setState({ isParsingCsv: false, csvUploadError: error || "Unable to read the uploaded file.", csvUploadMessage: "" }); return; }
            const uniqueMatchedIds = this.getMatchedStoreIds(values, filteredStores);
            if (uniqueMatchedIds.length === 0) { this.setState({ selectedStores: [], isParsingCsv: false, csvUploadMessage: "", csvUploadError: "No uploaded store numbers matched the selected chain." }); return; }

            const missingCount = values.length - uniqueMatchedIds.length;
            this.setState({
                selectedStores: uniqueMatchedIds,
                isParsingCsv: false,
                csvUploadError: "",
                csvUploadMessage: `${uniqueMatchedIds.length} store(s) selected from file.${missingCount > 0 ? ` ${missingCount} value(s) did not match stores for this chain.` : ""}`,
                storeSearchTerm: "",
                openStoreDropdown: false,
            });
        } catch (err) {
            console.error("Error parsing uploaded file:", err);
            const csvUploadError = err instanceof Error && err.message === "Uploaded Excel file has no sheets." ? err.message: "Unable to read the uploaded file.";
            this.setState({ isParsingCsv: false, csvUploadMessage: "", csvUploadError });
        } finally {
            inputEl.value = "";
        }
    };

    handleSubmit = async () => {
        const { selectedChainId, selectedStores, selectedRole, filteredStores } = this.state;
        const isChainFromStage = !!this.props.stage?.chainId;

        if (!selectedChainId) { alert("Please select a chain"); return; }
        if (isChainFromStage && selectedRole === "all") { alert("Please select a role"); return; }

        this.setState({ isSubmitting: true });
        try {
            const selectedStoreValues = selectedStores
                .map((id) => filteredStores.find((s) => s.id === id)?.retail_store_num)
                .filter(Boolean) as any[];

            let roleText: string | undefined;
            if (isChainFromStage) {
                const roleOptions = this.getRoleOptions();
                const matchedRole = roleOptions.find((opt) => opt.value === selectedRole);
                roleText = matchedRole ? matchedRole.label : undefined;
            }

            await this.props.onSubmit({
                chainId: selectedChainId,
                storeIds: selectedStoreValues,
                role: isChainFromStage ? selectedRole : "",
                roleText,
            });
            this.resetForm();
            this.props.onClose();
        } catch (error) {
            console.error("Error submitting assignment:", error);
        } finally {
            this.setState({ isSubmitting: false });
        }
    };

    handleClose = () => {
        if (!this.state.isSubmitting) {
            this.resetForm();
            this.props.onClose();
        }
    };

    filterStoresBySearch = (stores: Store[], searchTerm: string) => {
        if (!searchTerm.trim()) return stores;
        const lower = searchTerm.toLowerCase();
        return stores.filter(
            (store) =>
                store.name.toLowerCase().includes(lower) ||
                store.retail_store_num.toLowerCase().includes(lower)
        );
    };

    getChainOptions = () => {
        const { chains = [] } = this.props;
        return [
            { value: null, label: "Select Chain" },
            ...chains.map((chain: Chain) => ({ value: chain.id, label: chain.name })),
        ];
    };

    getStoreOptions = () => {
        const { filteredStores } = this.state;
        return filteredStores.map((store: Store) => ({
            value: store.id,
            label: `${store.retail_store_num}`,
        }));
    };

    getRoleOptions = () => {
        const { roles = [] } = this.props;
        return [
            { value: "all", label: "Select Role" },
            ...roles.map((role: Role) => ({ value: String(role.id), label: role.role })),
        ];
    };

    // Shared dropdown trigger 

    renderDropdownTrigger = (
        displayValue: string,
        placeholder: string,
        isOpen: boolean,
        disabled: boolean,
        onToggle: () => void
    ) => (
        <div
            className={`scss-dropdown-trigger ${disabled ? "scss-dropdown-trigger--disabled" : ""}`}
            onClick={disabled ? undefined : onToggle}
            onMouseEnter={(e) => {
                if (!disabled) (e.currentTarget as HTMLDivElement).style.borderColor = "#888";
            }}
            onMouseLeave={(e) => {
                if (!disabled) (e.currentTarget as HTMLDivElement).style.borderColor = "#D1D5DB";
            }}
        >
            <span className="scss-dropdown-trigger__text">
                {displayValue || placeholder}
            </span>
            <span
                className="scss-dropdown-trigger__arrow"
                style={{ transform: `translateY(-50%) rotate(${isOpen ? "270deg" : "90deg"})` }}
            >
                ›
            </span>
        </div>
    );

    // Single select 

    renderSelect = (
        label: string,
        value: number | string | null,
        isOpen: boolean,
        onToggle: () => void,
        onSelect: (value: any) => void,
        options: Array<{ value: any; label: string }>,
        dropdownRef: React.RefObject<HTMLDivElement>,
        disabled: boolean = false,
        searchTerm: string = "",
        onSearchChange: (term: string) => void
    ) => {
        const selectedOption =
            value !== null && value !== "all" ? options.find((opt) => opt.value === value) : null;
        const displayValue = selectedOption ? selectedOption.label : "";

        let filteredOptions = options.filter((opt) => opt.value !== null && opt.value !== "all");
        if (searchTerm.trim()) {
            const lower = searchTerm.toLowerCase();
            filteredOptions = filteredOptions.filter((opt) =>
                opt.label.toLowerCase().includes(lower)
            );
        }

        return (
            <div style={{ position: "relative" }} ref={dropdownRef}>
                {this.renderDropdownTrigger(displayValue, label, isOpen, disabled, onToggle)}

                {isOpen && !disabled && (
                    <div className="scss-dropdown-menu">
                        <div className="scss-dropdown-menu__search">
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => { e.stopPropagation(); onSearchChange(e.target.value); }}
                                onClick={(e) => e.stopPropagation()}
                                style={inlineStyles.searchInput}
                                autoFocus
                            />
                        </div>

                        {value !== null && value !== "all" && (
                            <div
                                className="scss-dropdown-menu__item scss-dropdown-menu__item--clear"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSelect(typeof value === "number" ? null : "all");
                                }}
                            >
                                Clear selection
                            </div>
                        )}

                        {filteredOptions.length === 0 ? (
                            <div className="scss-dropdown-menu__empty">No results found</div>
                        ) : (
                            filteredOptions.map((option) => (
                                <div
                                    key={option.value}
                                    className={`scss-dropdown-menu__item ${value === option.value ? "scss-dropdown-menu__item--selected" : ""}`}
                                    onClick={(e) => { e.stopPropagation(); onSelect(option.value); }}
                                    onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = "#f5f5f5"; }}
                                    onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = value === option.value ? "#f5f5f5" : "#fff"; }}
                                >
                                    {option.label}
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Multi select 

    renderMultiSelect = (
        label: string,
        selectedValues: number[],
        isOpen: boolean,
        onToggle: () => void,
        onToggleItem: (value: number) => void,
        options: Array<{ value: number; label: string }>,
        dropdownRef: React.RefObject<HTMLDivElement>,
        disabled: boolean = false,
        searchTerm: string = "",
        onSearchChange: (term: string) => void
    ) => {
        const displayText =
            selectedValues.length > 0 ? `${selectedValues.length} store(s) selected` : label;

        let filteredOptions = options;
        if (searchTerm.trim()) {
            const lower = searchTerm.toLowerCase();
            filteredOptions = options.filter((opt) => opt.label.toLowerCase().includes(lower));
        }

        const allSelected =
            selectedValues.length === filteredOptions.length && filteredOptions.length > 0;

        return (
            <div style={{ position: "relative" }} ref={dropdownRef}>
                {this.renderDropdownTrigger(
                    selectedValues.length > 0 ? displayText : "",
                    label,
                    isOpen,
                    disabled,
                    onToggle
                )}

                {isOpen && !disabled && (
                    <div className="scss-dropdown-menu">
                        <div className="scss-dropdown-menu__search">
                            <input
                                type="text"
                                placeholder="Search stores..."
                                value={searchTerm}
                                onChange={(e) => { e.stopPropagation(); onSearchChange(e.target.value); }}
                                onClick={(e) => e.stopPropagation()}
                                style={inlineStyles.searchInput}
                                autoFocus
                            />
                        </div>

                        {options.length > 0 && (
                            <div
                                className="scss-dropdown-menu__item scss-dropdown-menu__item--select-all"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (allSelected) this.handleClearAllStores();
                                    else this.handleSelectAllStores();
                                }}
                            >
                                {allSelected ? "Clear All" : `Select All${searchTerm ? " (Filtered)" : ""}`}
                            </div>
                        )}

                        {filteredOptions.length === 0 ? (
                            <div className="scss-dropdown-menu__empty">
                                {options.length === 0 ? "No stores available for this chain" : "No results found"}
                            </div>
                        ) : (
                            filteredOptions.map((option) => {
                                const isSelected = selectedValues.includes(option.value);
                                return (
                                    <div
                                        key={option.value}
                                        className="scss-dropdown-menu__item scss-dropdown-menu__item--checkbox"
                                        onClick={(e) => { e.stopPropagation(); onToggleItem(option.value); }}
                                        onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = "#f5f5f5"; }}
                                        onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = "#fff"; }}
                                    >
                                        <div
                                            style={{
                                                width: "16px",
                                                height: "16px",
                                                border: `2px solid ${isSelected ? "#E5B611" : "#D1D5DB"}`,
                                                borderRadius: "3px",
                                                backgroundColor: isSelected ? "#E5B611" : "#fff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                transition: "all 0.2s",
                                                flexShrink: 0,
                                            }}
                                        >
                                            {isSelected && (
                                                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                                    <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            )}
                                        </div>
                                        <span>{option.label}</span>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </div>
        );
    };

    render() {
        const {
            selectedChainId, selectedStores, selectedRole,
            openChainDropdown, openStoreDropdown, openRoleDropdown,
            isSubmitting, isLoadingStores, filteredStores,
            chainSearchTerm, storeSearchTerm, roleSearchTerm,
            isParsingCsv, csvUploadMessage, csvUploadError,
        } = this.state;

        const isChainSelected = selectedChainId !== null;
        const isChainFromStage = !!this.props.stage?.chainId;
        const chainOptions = this.getChainOptions();
        const storeOptions = this.getStoreOptions();
        const roleOptions = this.getRoleOptions();

        const isSubmitDisabled =
            !selectedChainId ||
            (isChainFromStage && selectedRole === "all") ||
            isSubmitting;

        const modalFooter = (
            <div className="scss-modal-footer">
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <span
                        style={{
                            display: "inline-flex", width: 20, height: 20,
                            alignItems: "center", justifyContent: "center",
                            visibility: isSubmitting ? "visible" : "hidden",
                        }}
                        aria-hidden={!isSubmitting}
                    >
                        {isSubmitting && <Loader height={20} width={20} custom={true} />}
                    </span>
                    <button
                        className="primary-button"
                        onClick={this.handleSubmit}
                        disabled={isSubmitDisabled}
                        aria-label={isSubmitting ? "Submitting request" : "Submit request"}
                        aria-busy={isSubmitting || undefined}
                        style={{
                            opacity: isSubmitDisabled ? 0.5 : 1,
                            cursor: isSubmitDisabled ? "not-allowed" : "pointer",
                            minWidth: "120px",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {isSubmitting
                            ? this.props.stage?.chainId ? "Submitting..." : "Approving..."
                            : this.props.stage?.chainId ? "Submit" : "Approve"}
                    </button>
                </div>
                <button
                    className="primary-button"
                    onClick={this.handleClose}
                    disabled={isSubmitting}
                    style={{ minWidth: "120px" }}
                >
                    Cancel
                </button>
            </div>
        );

        return (
            <CommonModal
                show={this.props.isOpen}
                onClose={this.handleClose}
                title=""
                subTitle=""
                width="600px"
                closeOnBackdropClick={!isSubmitting}
                footer={modalFooter}
            >
                {/* Header */}
                <div style={inlineStyles.modalHeader}>
                    <h2 style={{ fontWeight: 500, fontSize: "16px", margin: 0 }}>
                        Assign Chain and Store
                    </h2>
                </div>

                <p style={inlineStyles.modalMessage}>
                    Assign appropriate chain and store for this user
                </p>

                <div style={{ borderBottom: "1px solid #F0F2F5", marginBottom: "15px" }} />

                {/* Scrollable content */}
                <div className="scss-modal-scroll">
                    <div style={inlineStyles.modalFormCard}>
                        <div className="scss-modal-form">

                            {/* Chain */}
                            <div className="scss-modal-field">
                                <label style={inlineStyles.modalLabel}>
                                    Which chain are you providing access to? *
                                </label>
                                {this.renderSelect(
                                    "Select Chain",
                                    selectedChainId,
                                    openChainDropdown,
                                    () => this.setState({ openChainDropdown: !openChainDropdown }),
                                    this.handleChainFilterChange,
                                    chainOptions,
                                    this.chainDropdownRef,
                                    false,
                                    chainSearchTerm,
                                    (term) => this.setState({ chainSearchTerm: term })
                                )}
                            </div>

                            {/* Stores */}
                            <div className="scss-modal-field">
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                                <label style={inlineStyles.modalLabel}>
                                    Which store are you providing access to?
                                </label>
                                <input ref={this.csvFileInputRef} type="file" accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" style={{ display: "none" }} onChange={this.handleCsvUpload}/>
                                </div>

                                <div style={{ display: "flex", gap: "12px" }}>
                                    <button type="button" style={{ backgroundColor: "#E5B42F", color: "#0F0F0F", border: "none", borderRadius: "4px", padding: "6px 10px", fontSize: "12px", fontWeight: 500, cursor: isParsingCsv ? "wait" : "pointer", whiteSpace: "nowrap", opacity: isParsingCsv ? 0.8 : 1 }} onClick={() => this.csvFileInputRef.current?.click()} disabled={isParsingCsv} >
                                        {isParsingCsv ? "Parsing CSV..." : "Upload Store CSV"}
                                    </button>
                                    <button
                                        type="button"
                                        style={{ fontSize: "12px", color: "#E5B42F", display: "inline-flex", textDecoration: "underline", cursor: "pointer", background: "transparent", border: "none", padding: 0 }}
                                        onClick={() => {
                                            const link = document.createElement("a");
                                            link.href = "data:text/csv;charset=utf-8,retail_store_number%0A";
                                            link.download = "store-access-template.csv";
                                            link.click();
                                        }}
                                    >
                                        Download CSV Template
                                    </button>
                                </div>
                                {csvUploadMessage && <div style={{ fontSize: "11px", color: "#0F0F0F", marginTop: "6px" }}>{csvUploadMessage}</div>}
                                {csvUploadError && <div style={{ fontSize: "11px", color: "#0F0F0F", marginTop: "6px" }}>{csvUploadError}</div>}

                                {isLoadingStores ? (
                                    <div className="scss-store-loading">
                                        <svg
                                            width="14" height="14" viewBox="0 0 14 14"
                                            style={{ animation: "spin 1s linear infinite", flexShrink: 0 }}
                                        >
                                            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                                            <circle cx="7" cy="7" r="5" stroke="#E5B611" strokeWidth="2" fill="none" strokeDasharray="25" strokeLinecap="round" />
                                        </svg>
                                        Fetching stores, please wait...
                                    </div>
                                ) : (
                                    this.renderMultiSelect(
                                        "Select Store(s)",
                                        selectedStores,
                                        openStoreDropdown,
                                        () => this.setState({ openStoreDropdown: !openStoreDropdown }),
                                        this.handleStoreToggle,
                                        storeOptions,
                                        this.storeDropdownRef,
                                        !isChainSelected || isLoadingStores,
                                        storeSearchTerm,
                                        (term) => this.setState({ storeSearchTerm: term })
                                    )
                                )}

                                {selectedStores.length > 0 && !isLoadingStores && (
                                    <div
                                        className="scss-chips-container"
                                        style={{
                                            maxHeight: selectedStores.length > 4 ? "100px" : "auto",
                                            overflowY: selectedStores.length > 4 ? "auto" : "hidden",
                                        }}
                                    >
                                        {selectedStores.map((storeId) => {
                                            const store = filteredStores.find((s) => s.id === storeId);
                                            return (
                                                <div key={storeId} style={inlineStyles.selectedStoreChip}>
                                                    <span>{store?.retail_store_num}</span>
                                                    <button
                                                        style={inlineStyles.removeStoreButton}
                                                        onClick={(e) => { e.stopPropagation(); this.handleRemoveStore(storeId); }}
                                                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "rgba(255,255,255,0.2)"; }}
                                                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent"; }}
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Role */}
                            {isChainSelected && isChainFromStage && (
                                <div className="scss-modal-field">
                                    <label style={inlineStyles.modalLabel}>Select Role *</label>
                                    {this.renderSelect(
                                        "Select Role",
                                        selectedRole === "all" ? null : selectedRole,
                                        openRoleDropdown,
                                        () => this.setState((prev) => ({
                                            openRoleDropdown: !prev.openRoleDropdown,
                                            roleSearchTerm: "",
                                        })),
                                        this.handleRoleFilterChange,
                                        roleOptions,
                                        this.roleDropdownRef,
                                        false,
                                        roleSearchTerm,
                                        (term) => this.setState({ roleSearchTerm: term })
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Info pill */}
                        <div style={inlineStyles.infoPill}>
                            <span style={{
                                width: 16, height: 16, borderRadius: "50%",
                                border: "2px solid #0755AA", color: "#0755AA",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 11, fontWeight: "bold", lineHeight: 1, flexShrink: 0,
                            }}>
                                !
                            </span>
                            <span>Requests will be sent to next approver.</span>
                        </div>
                    </div>
                </div>
            </CommonModal>
        );
    }
}

const connector = connect(
    (state: State) => ({
        chains: state.chains.content || [],
        stores: state.stores.content?.data || [],
        user: state.auth_user.content,
        roles: state.chains_users_role.roles || [],
    }),
    { fetchChains, fetchStores, getRoles }
);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(SelectChainStoreInfo);